import {
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button
} from '@mui/material'
import React, { useCallback } from 'react'
import { ErrorAlert } from '../formik/ErrorAlert'
import { UserState } from '../state/user'
import { useFormik } from 'formik'
import { RefreshActions } from '../state/refresh'
import {
  validateMail,
  validateName,
  validateUsername
} from '../utils/validators'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../requests/useRequests'
import { State } from '../types/state'
import { useIsMobileView } from '../hooks/useIsMobileView'

const UserDetails = (): JSX.Element => {
  const dispatch = useDispatch()
  const isMobileView = useIsMobileView()
  const requestMaker = useRequests()
  const userState = useSelector((state: State) => state.user)

  const validate = (user: Partial<UserState>): Partial<UserState> => {
    const errors: typeof user = {}

    validateUsername(user.username ?? null, e => (errors.username = e))
    validateMail(user.email ?? null, e => (errors.email = e))
    validateName(user.firstName ?? null, e => (errors.firstName = e))
    validateName(user.lastName ?? null, e => (errors.lastName = e))

    return errors
  }

  const onSubmit = useCallback(
    async (user: UserState): Promise<void> => {
      try {
        await requestMaker.updateUserDetails({
          username: user.username!,
          email: user.email!,
          firstName: user.firstName!,
          lastName: user.lastName!
        })
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Succesfully updated user details'
          })
        )
        dispatch(RefreshActions.refreshUserState())
      } catch (e) {
        dispatch(
          RefreshActions.setToastMessage({
            message:
              'Error - fill all neccessary fields correctly and try again'
          })
        )
      }
    },
    [requestMaker, dispatch]
  )

  const formik = useFormik({
    initialValues: userState,
    onSubmit,
    enableReinitialize: true,
    validate
  })

  const handleChangeUsername = async (username: string): Promise<void> => {
    void formik.setFieldValue('username', username)
    if (username === userState.username) {
      return
    }

    const response = await requestMaker.emailOrUsernameExists(username)

    if (response.data) {
      void formik.setFieldError('username', 'User with this username exists')
    } else {
      void formik.setFieldError('username', undefined)
    }
    void formik.setFieldError('username', undefined)
  }

  const handleEmailBlur = async (email: string): Promise<void> => {
    if (email === userState.email) {
      return
    }

    const response = await requestMaker.emailExists(email)

    if (response.data) {
      void formik.setFieldError('email', 'Email exists')
    } else {
      void formik.setFieldError('email', undefined)
    }
  }

  return (
    <form>
      <div style={{ width: isMobileView ? '80vw' : '40vw', margin: 'auto' }}>
        <DialogTitle>User details</DialogTitle>
        <DialogContent>
          <TextField
            error={formik.errors.username !== undefined}
            variant='filled'
            helperText={formik.errors.username}
            value={formik.values.username}
            onChange={e => {
              void handleChangeUsername(e.target.value)
            }}
            onBlur={e => {
              void handleChangeUsername(e.target.value)
            }}
            margin='normal'
            required
            fullWidth
            id='username'
            label='Username'
            name='username'
            autoComplete='username'
            autoFocus
          />
          <TextField
            error={formik.errors.firstName !== undefined}
            variant='filled'
            helperText={formik.errors.firstName}
            value={formik.values.firstName}
            onChange={formik.handleChange}
            margin='normal'
            fullWidth
            id='firstName'
            label='First Name'
            name='firstName'
            autoComplete='firstName'
          />
          <TextField
            error={formik.errors.lastName !== undefined}
            variant='filled'
            helperText={formik.errors.lastName}
            value={formik.values.lastName}
            onChange={formik.handleChange}
            margin='normal'
            fullWidth
            id='lastName'
            label='Last Name'
            name='lastName'
            autoComplete='lastName'
          />
          <TextField
            margin='dense'
            id='email'
            label='Email'
            type='email'
            fullWidth
            variant='standard'
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={e => {
              void handleEmailBlur(e.target.value)
            }}
            style={{ marginBottom: '3vh' }}
            required
          />
          <ErrorAlert formik={formik} name='email' />
        </DialogContent>
      </div>

      <DialogActions>
        <Button
          onClick={formik.handleSubmit as any}
          style={{ margin: 'auto' }}
          variant='contained'
        >
          Update
        </Button>
      </DialogActions>
    </form>
  )
}

export default UserDetails
